<template>
  <button
    :type="type"
    class="ui-button base-border-radius"
    :disabled="disabled || loading"
    :class="classes"
    v-bind="$attrs"
    @click="$emit('click', $event)"
  >
    <span class="row" :class="{ 'opacity-0': loading }">
      <Icon v-if="icon && $slots.default" :name="icon" :size="iconSize" />
      <slot />
      <template v-if="!$slots.default">
        <Icon v-if="icon" :name="icon" :size="iconSize" />
        <q-icon v-if="prependIcon" class="ui-button__icon" :name="prependIcon" />
        <span v-html="label"></span>
        <Icon v-if="iconRight" :name="iconRight" :size="iconSize" />
        <q-icon v-if="appendIcon" class="ui-button__icon" :name="appendIcon" />
      </template>
    </span>
    <slot name="icon" />
    <span v-if="loading" class="absolute absolute-vh-middle">
      <q-spinner size="md" />
    </span>
  </button>
</template>

<script>
export default {
  name: 'UIButton',
  props: {
    type: {
      type: String,
      default: 'button',
    },
    reverse: Boolean,
    icon: {
      type: String,
      default: null,
    },
    iconRight: {
      type: String,
      default: null,
    },
    iconSize: {
      type: Number,
      default: 24,
    },
    prependIcon: {
      type: String,
      default: null,
    },
    appendIcon: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    solid: {
      type: Boolean,
      default: true,
    },
    noWrap: {
      type: Boolean,
      default: true,
    },
    color: {
      type: String,
      default: 'cas-button',
    },
    small: {
      type: Boolean,
      default: false,
    },
    fab: {
      type: Boolean,
      default: false,
    },
    flat: {
      type: Boolean,
      default: false,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    primary: {
      type: Boolean,
      default: false,
    },
    nav: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  computed: {
    classes() {
      let classes = [`ui-button--${this.solid ? 'solid' : 'outlined'}`]
      if (this.solid) {
        classes.push(`bg-${this.color}`)
        if (!this.primary) {
          classes.push('text-white')
        }

        if (this.color.includes('opacity')) {
          classes.push('border-transparent')
        } else {
          classes.push(`border-${this.color}`)
        }
      } else {
        classes.push(`text-${this.color}`)
        classes.push('bg-transparent')
        classes.push(`border-${this.color}`)
      }
      const buttonDisabled = this.disabled ? true : this.loading
      if (!buttonDisabled) {
        classes.push('cursor-pointer')
      }
      if (this.noWrap) {
        classes.push('text-no-wrap')
      }
      if (this.small) {
        classes.push('ui-button--small')
      }
      if (this.fab) {
        classes.push('ui-button--fab')
      }
      if (this.flat) {
        classes.push('ui-button--flat')
      }

      if (this.secondary) {
        classes.push('ui-button--secondary')
      }
      if (this.primary) {
        classes.push('ui-button--primary')
      }
      if (this.nav) {
        classes.push('ui-button--nav')
      }

      if (this.$attrs.class) {
        classes.push(this.$attrs.class)
      }

      return classes.join(' ')
    },
  },
}
</script>

<style lang="scss">
.ui-button {
  position: relative;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  font-size: 16px;
  outline: none;
  border-style: solid;
  border-width: 1px;
  font-weight: 500;
  flex: none;
  color: var(--cas-dark);
  .row {
    gap: 8px;
    flex-wrap: nowrap;
    display: flex;
    align-items: center;
  }
  &--small {
    height: 36px;
  }

  &--fab {
    width: 50px;
    padding: 5px;

    &.ui-button--small {
      height: 32px;
      width: 32px;
      padding: 4px;
    }
  }

  &--flat {
    background: transparent !important;
    border: none !important;
    color: var(--cas-light-700) !important;
  }

  &__icon {
    font-size: 24px;
  }

  &:hover:not([disabled]) {
    transition: 0.3s;
    filter: brightness(1.1);
  }

  &--outlined {
    &:hover:not([disabled]) {
      background: rgba(255, 255, 255, 0.03) !important;
    }
  }

  &:hover:not([disabled]) {
    filter: brightness(1.14);
    &.bg-opacity {
      background: rgba(255, 255, 255, 0.14) !important;
    }
  }

  &:active:not([disabled]) {
    filter: brightness(1.28);
    &.bg-opacity {
      background: rgba(255, 255, 255, 0.28) !important;
    }
  }

  &--outlined {
    &:hover:not([disabled]) {
      background: rgba(255, 255, 255, 0.03) !important;
    }
  }
  &--secondary {
    background-color: var(--cas-light-100) !important;
    border: none !important;
    &:hover {
      background-color: var(--cas-light-200) !important;
    }
    &:active {
      background-color: var(--cas-light-300) !important;
    }
  }
  &--primary {
    border: none !important;
    color: var(--cas-dark) !important;
  }

  &--nav {
    height: 32px;
    padding-left: 0;
    padding-right: 0;
  }
}

//@media (max-width: 767px) {
//  .ui-button {
//    height: 44px;
//    padding: 8px 20px;
//    font-size: 14px;
//  }
//}

@media (max-width: 575px) {
  .ui-button {
    //height: 40px;
    padding: 0 18px;

    &--nav {
      padding-left: 0;
      padding-right: 0;
    }
  }
}
</style>
